import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, Sparkles, FileText, Download, Trash2, ExternalLink, DollarSign, Building2, ChevronRight } from 'lucide-react';
import { getAiProviderArgs } from '../../lib/aiProviderPicker';
import AddLinkButton from '../AddLinkButton';
import MoneyInput from '../MoneyInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n)); }
  catch { return `$${n}`; }
};

/**
 * CapExTab — dedicated tab inside PropertyBudgetsDialog for capital
 * expenditure documents. Two columns:
 *
 *   LEFT  — upload + list of every existing budget doc tagged `cap-ex`
 *   RIGHT — AI-driven CapEx document generator (gpt-5.2). The generated
 *           doc can be saved straight into the budgets list with one click.
 *
 * Why a dedicated tab? CapEx is the only budget category the GM regularly
 * needs to author from scratch (vs the others which are uploads of
 * existing PDFs). Surfacing the AI generator inline removes the back-and-
 * forth between the dialog and a separate AI window.
 */
export default function CapExTab({ adminToken, budgets, onReload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  // AI form
  const [projectName, setProjectName] = useState('');
  const [scope, setScope] = useState('');
  const [totalBudget, setTotalBudget] = useState('');
  const [timeline, setTimeline] = useState('12');
  const [funding, setFunding] = useState('');
  const [notes, setNotes] = useState('');
  // iter-79: hotel-specific parameters that materially change the
  // line-item breakdown — keys (= room count), F&B outlets, public-
  // space scope, contingency %, escalation %, soft-cost %. Sent to
  // the AI so it can size buckets like FF&E, OS&E, building, MEP and
  // pre-opening realistically per the resort's actual scale.
  const [keyCount, setKeyCount] = useState('');
  const [fnbOutlets, setFnbOutlets] = useState('');
  const [contingencyPct, setContingencyPct] = useState('10');
  const [escalationPct, setEscalationPct] = useState('3');
  const [softCostPct, setSoftCostPct] = useState('12');
  // Generated result
  const [result, setResult] = useState(null);

  const adminAuth = adminToken
    ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true }
    : null;

  // Filter the parent list of all budgets down to CapEx only.
  const capexBudgets = useMemo(
    () => (budgets || []).filter((b) => b.category === 'cap-ex'),
    [budgets]
  );
  const capexTotal = useMemo(
    () => capexBudgets.reduce((acc, b) => acc + (Number(b.amount) || 0), 0),
    [capexBudgets]
  );

  // ─── Upload an existing CapEx file (PDF, XLSX, image…) ────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!adminToken) { toast.error('Sign in as admin to upload CapEx files.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('keep_raw', 'true');
      const { data: upload } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const defaultTitle = file.name.replace(/\.[^.]+$/, '');
      const title = window.prompt('CapEx document title:', defaultTitle);
      if (!title) { setUploading(false); if (fileRef.current) fileRef.current.value = ''; return; }
      const amountRaw = window.prompt('Headline amount in USD (optional):', '');
      const amount = amountRaw && !Number.isNaN(parseFloat(amountRaw)) ? parseFloat(amountRaw) : null;
      await axios.post(`${API}/admin/property-budgets`, {
        title,
        category: 'cap-ex',
        amount,
        file_url: `${BACKEND}${upload.url}`,
        file_name: file.name,
        file_type: ext,
        order: (budgets || []).length,
      }, adminAuth);
      toast.success('CapEx document uploaded.');
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ─── AI generate ──────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!projectName.trim()) { toast.error('Project name is required.'); return; }
    if (!adminAuth) { toast.error('Sign in as admin to use AI.'); return; }
    const providerArgs = await getAiProviderArgs();
    if (providerArgs === null) return; // cancelled
    setGenerating(true);
    setResult(null);
    try {
      const body = {
        project_name: projectName.trim(),
        scope: scope.trim() || null,
        total_budget_usd: totalBudget.trim() ? parseFloat(totalBudget) : null,
        timeline_months: parseInt(timeline, 10) || 12,
        funding_source: funding.trim() || null,
        ...providerArgs,
        // iter-79: roll the hotel parameters into the notes field so we
        // don't need a backend schema change. The AI prompt is wide
        // enough that these get treated as first-class context.
        notes: [
          notes.trim() || null,
          keyCount && `Keys (room count): ${keyCount}`,
          fnbOutlets && `F&B outlets: ${fnbOutlets}`,
          contingencyPct && `Contingency: ${contingencyPct}%`,
          escalationPct && `Escalation: ${escalationPct}%`,
          softCostPct && `Soft costs: ${softCostPct}%`,
        ].filter(Boolean).join(' | ') || null,
      };
      const { data } = await axios.post(`${API}/admin/ai/generate-capex`, body, adminAuth);
      setResult(data);
      toast.success('CapEx document drafted.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ─── Save AI result to the budgets list as a `cap-ex` markdown doc ───────
  const handleSaveAiDoc = async () => {
    if (!result || !adminAuth) return;
    setSaving(true);
    try {
      // Convert the markdown to a Blob, upload it, then register the
      // budget entry pointing at the file. We use the existing upload
      // pipeline so the file flows through object-storage mirroring.
      const md = result.full_text || '';
      const fname = `capex-${(result.project_name || 'document').toLowerCase().replace(/[^a-z0-9-]+/g, '-')}-${Date.now()}.md`;
      const fd = new FormData();
      fd.append('file', new Blob([md], { type: 'text/markdown' }), fname);
      fd.append('keep_raw', 'true');
      const { data: upload } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      await axios.post(`${API}/admin/property-budgets`, {
        title: result.title || result.project_name,
        category: 'cap-ex',
        amount: result.computed_total_usd ?? result.total_budget_usd ?? null,
        description: result.summary || '',
        file_url: `${BACKEND}${upload.url}`,
        file_name: fname,
        file_type: 'md',
        order: (budgets || []).length,
      }, adminAuth);
      toast.success('AI CapEx document saved to budgets list.');
      onReload?.();
      // Clear the form so the next draft starts fresh.
      setResult(null);
      setProjectName('');
      setScope('');
      setTotalBudget('');
      setFunding('');
      setNotes('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    if (!adminAuth) return;
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/property-budgets/${b.id}`, adminAuth);
      onReload?.();
    } catch (err) {
      toast.error('Delete failed.');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="capex-tab">
      {/* ─── LEFT: existing CapEx files + uploader ─────────────────────────── */}
      <div className="lg:col-span-2 rounded-2xl p-4" style={{ backgroundColor: 'white', border: '1px solid #E8DFC6' }}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>CapEx Library</p>
              <p className="text-sm font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                {capexBudgets.length} document{capexBudgets.length === 1 ? '' : 's'} · {fmtUSD(capexTotal)}
              </p>
            </div>
          </div>
          <button
            data-testid="capex-upload-btn"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition disabled:opacity-50"
            style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
          <AddLinkButton
            testid="capex-add-link-btn"
            label="Add link"
            onAdd={async ({ url, label }) => {
              try {
                await axios.post(`${API}/admin/property-budgets`, {
                  title: label || 'External CapEx link',
                  category: 'cap-ex',
                  file_url: url,
                  file_name: label || url,
                  file_type: 'link',
                  order: (budgets || []).length,
                }, adminAuth);
                toast.success('Link added to CapEx library.');
                onReload?.();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Could not save link.');
              }
            }}
          />
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.md,.txt,.doc,.docx"
            onChange={handleUpload}
            data-testid="capex-upload-input"
          />
        </div>

        {capexBudgets.length === 0 ? (
          <div className="text-center py-8 rounded-lg" style={{ backgroundColor: '#F8F5EC', color: '#7A8C8C', fontFamily: 'Manrope' }}>
            <FileText className="w-7 h-7 mx-auto mb-1 opacity-50" />
            <p className="text-xs">No CapEx documents yet.</p>
            <p className="text-[10px] mt-0.5">Upload an existing PDF/XLSX, or draft one with AI on the right.</p>
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="capex-list">
            {capexBudgets.map((b) => (
              <li
                key={b.id}
                data-testid={`capex-row-${b.id}`}
                className="group rounded-lg p-2.5 flex items-center gap-2 transition"
                style={{ backgroundColor: '#F8F5EC' }}
              >
                <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#003DA5' }} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{b.title}</p>
                  <p className="text-[10px]" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
                    {fmtUSD(b.amount)} {b.file_name ? `· ${b.file_name}` : ''}
                  </p>
                </div>
                {b.file_url && (
                  <a
                    href={b.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-white transition"
                    title="Open"
                    data-testid={`capex-open-${b.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: '#003DA5' }} />
                  </a>
                )}
                <button
                  onClick={() => handleDelete(b)}
                  className="p-1.5 rounded hover:bg-red-100 transition opacity-0 group-hover:opacity-100"
                  title="Delete"
                  data-testid={`capex-delete-${b.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ─── RIGHT: AI generator ───────────────────────────────────────────── */}
      <div className="lg:col-span-3 rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)', border: '1px solid #FDE68A' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}>
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#6366F1', fontFamily: 'Manrope' }}>Heavenly AI · Draft from scratch</p>
            <p className="text-sm font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>Generate a board-ready CapEx document</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
          <Field label="Project name *" testid="capex-ai-project">
            <input
              data-testid="capex-ai-project-input"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. East Wing Atrium Restoration"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Total budget (USD, optional)" testid="capex-ai-total">
            <MoneyInput
              data-testid="capex-ai-total-input"
              value={totalBudget}
              onChange={(e) => setTotalBudget(e.target.value)}
              placeholder="e.g. 2,400,000"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Timeline (months)" testid="capex-ai-timeline">
            <input
              data-testid="capex-ai-timeline-input"
              type="number"
              value={timeline}
              onChange={(e) => setTimeline(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Funding source" testid="capex-ai-funding">
            <input
              data-testid="capex-ai-funding-input"
              value={funding}
              onChange={(e) => setFunding(e.target.value)}
              placeholder="e.g. Insurance proceeds + owner equity"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
        </div>
        {/* iter-79: hotel-specific sizing parameters. Shown in a 5-up
            row so the AI prompt receives the realistic shape of the
            resort (rooms / F&B outlets / contingency / escalation /
            soft-costs). */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-2">
          <Field label="Keys" testid="capex-ai-keys">
            <input
              data-testid="capex-ai-keys-input"
              type="number"
              value={keyCount}
              onChange={(e) => setKeyCount(e.target.value)}
              placeholder="e.g. 314"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="F&B outlets" testid="capex-ai-fnb">
            <input
              data-testid="capex-ai-fnb-input"
              type="number"
              value={fnbOutlets}
              onChange={(e) => setFnbOutlets(e.target.value)}
              placeholder="e.g. 6"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Contingency %" testid="capex-ai-contingency">
            <input
              data-testid="capex-ai-contingency-input"
              type="number"
              value={contingencyPct}
              onChange={(e) => setContingencyPct(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Escalation %" testid="capex-ai-escalation">
            <input
              data-testid="capex-ai-escalation-input"
              type="number"
              value={escalationPct}
              onChange={(e) => setEscalationPct(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
          <Field label="Soft costs %" testid="capex-ai-soft">
            <input
              data-testid="capex-ai-soft-input"
              type="number"
              value={softCostPct}
              onChange={(e) => setSoftCostPct(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
            />
          </Field>
        </div>
        <Field label="Scope" testid="capex-ai-scope">
          <textarea
            data-testid="capex-ai-scope-input"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            rows={2}
            placeholder="Demolition + structural repair of the East Wing atrium, including new curtain wall, HVAC, lighting…"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
          />
        </Field>
        <Field label="Additional notes" testid="capex-ai-notes">
          <textarea
            data-testid="capex-ai-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Hurricane contingency, contractor markup, escalation, etc."
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: 'white', border: '1px solid #E8DFC6', fontFamily: 'Manrope' }}
          />
        </Field>

        <button
          data-testid="capex-ai-generate"
          onClick={handleGenerate}
          disabled={generating || !projectName.trim()}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: 'white', fontFamily: 'Manrope' }}
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Drafting…' : 'Generate CapEx document'}
        </button>

        {/* ─── Generated preview ───────────────────────────────────────────── */}
        {result && (
          <div data-testid="capex-ai-result" className="mt-4 rounded-xl p-4 space-y-3" style={{ backgroundColor: 'white', border: '1px solid #C9D6E6' }}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{result.title || result.project_name}</p>
              <span className="text-xs font-bold flex items-center gap-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                <DollarSign className="w-3.5 h-3.5" />
                {fmtUSD(result.computed_total_usd)}
              </span>
            </div>
            {result.summary && (
              <p className="text-[12px] leading-relaxed italic" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{result.summary}</p>
            )}
            {Array.isArray(result.line_items) && result.line_items.length > 0 && (
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #E8DFC6' }}>
                <table className="w-full text-[11px]">
                  <thead style={{ backgroundColor: '#F8F5EC' }}>
                    <tr>
                      <th className="text-left px-2 py-1.5 font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Category</th>
                      <th className="text-left px-2 py-1.5 font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Description</th>
                      <th className="text-right px-2 py-1.5 font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.line_items.map((li, idx) => (
                      <tr key={idx} className="border-t" style={{ borderColor: '#F0E7D0' }}>
                        <td className="px-2 py-1.5" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{li.category}</td>
                        <td className="px-2 py-1.5" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{li.description}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{fmtUSD(li.amount_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#FEF3C7' }}>
                      <td colSpan={2} className="px-2 py-1.5 font-bold" style={{ color: '#92400E', fontFamily: 'Manrope' }}>Total</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold" style={{ color: '#92400E', fontFamily: 'Manrope' }}>{fmtUSD(result.computed_total_usd)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <details className="rounded-lg" style={{ border: '1px solid #E8DFC6' }}>
              <summary className="cursor-pointer px-3 py-2 text-xs font-bold flex items-center gap-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                <ChevronRight className="w-3 h-3" /> Full markdown preview
              </summary>
              <pre className="px-3 py-2 text-[10.5px] whitespace-pre-wrap leading-snug" style={{ color: '#1A2E2E', fontFamily: 'ui-monospace,Menlo,monospace' }}>{result.full_text || ''}</pre>
            </details>

            <div className="flex items-center gap-2">
              <button
                data-testid="capex-ai-save"
                onClick={handleSaveAiDoc}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition disabled:opacity-50"
                style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {saving ? 'Saving…' : 'Save to CapEx library'}
              </button>
              <button
                data-testid="capex-ai-discard"
                onClick={() => setResult(null)}
                className="text-xs font-bold px-3 py-2 rounded-lg"
                style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block mb-2" data-testid={testid}>
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>{label}</span>
      {children}
    </label>
  );
}
